import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {concatWithBrandName} from "../../../../app/utils/concat_with_brand_name";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType} from "./constants";

export const studioOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        studioPriceMin: props.region.stats?.properties_studios_ranges_price_min || 0,
        studiosRangesPriceM2Avg: props.region.stats?.properties_studios_ranges_price_m2_avg || 0,
        studiosCountReady: props.region.stats?.properties_studios_count_ready || 0,
        studiosCountReadyInThreeMonths: props.region.stats?.properties_studios_count_ready_in_3m || 0,
        studiosCountReadyInSixMonths: props.region.stats?.properties_studios_count_ready_in_6m || 0,
        studiosCountReadyInOneYear: props.region.stats?.properties_studios_count_ready_in_12m || 0,
        studiosGroundFloor: props.region.stats?.properties_studios_ground_floor || 0,
        studiosGroundFloorWithGarden: props.region.stats?.properties_studios_ground_floor_with_garden || 0,
        studiosHighFloor: props.region.stats?.properties_studios_high_floor || 0,
        studiosLastFloor: props.region.stats?.properties_studios_last_floor || 0,
        studiosLastFloorWithTerrace: props.region.stats?.properties_studios_last_floor_with_terrace || 0,
        studiosLowFloor: props.region.stats?.properties_studios_low_floor || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        studiosOffersCountForSale: props.region.stats?.offers_studios_count_for_sale || 0,
        studiosPropertiesCountForSale: props.region.stats?.properties_studios_count_for_sale || 0
    };

    const getGeneralPropertyCountText = (propertiesCount: number) => {
        return `${propertiesCount} ${pluralize(["lokal", "lokale", "lokali"])(propertiesCount)}`;
    };

    if (!variables.studiosOffersCountForSale || !variables.studiosPropertiesCountForSale) {
        return [];
    }

    return [
        {
            headline: `Nowe kawalerki na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.studiosOffersCountForSale,
                    propertiesCountForSale: variables.studiosPropertiesCountForSale,
                    offerTypeName: "kawalerek"
                })
            ]
        },
        {
            headline: `Ile kosztują nowe kawalerki ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.studioPriceMin > 0 &&
                            `Najtańsza kawalerka ${variables.regionNameDeclensionWhere} dostępna w sprzedaży kosztuje ${priceFormat(
                                variables.studioPriceMin
                            )}.`,
                        ` `,
                        variables.studiosRangesPriceM2Avg > 0 &&
                            `Aktualnie średnia cena za m2 kawalerki ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(
                                variables.studiosRangesPriceM2Avg
                            )}.`
                        // TODO: waiting to next stage
                        // ${getEstimatedPriceText(props.selectedRegionEstimatedPrices)}
                    ].filter(Boolean) as string[]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym  ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)} na kawalerki.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na kawalerki.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - kawalerka gotowa do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.studiosCountReady,
                    offersCountReadyInThreeMonths: variables.studiosCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.studiosCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.studiosCountReadyInOneYear,
                    offerTypeNamePluralize: ["kawalerkę", "kawalerki", "kawalerek"],
                    offerTypeNameWithDeclensionOfReadyPluralize: ["kawalerka zostanie oddana", "kawalerki zostaną oddane", "kawalerek zostanie oddanych"],
                    offersLatestConstructionDate: variables.flatsLatestConstructionDate
                })
            ]
        },
        ...(variables.studiosGroundFloor > 0 ||
        variables.studiosGroundFloorWithGarden > 0 ||
        variables.studiosLowFloor > 0 ||
        variables.studiosHighFloor > 0 ||
        variables.studiosLastFloor > 0 ||
        variables.studiosLastFloorWithTerrace > 0
            ? ([
                  {
                      headline: `Oferty kawalerek dostępne na portalu`,
                      paragraphs: [
                          {
                              type: ParagraphType.TEXT,
                              text: `${concatWithBrandName("Na portalu")} dostępne są oferty kawalerek ${
                                  variables.regionNameDeclensionWhere
                              }, dostosowane do potrzeb odbiorców, w tym:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  variables.studiosGroundFloor > 0 && `kawalerki na parterze - ${getGeneralPropertyCountText(variables.studiosGroundFloor)}`,
                                  variables.studiosGroundFloorWithGarden > 0 &&
                                      `kawalerki na parterze z ogródkiem - ${getGeneralPropertyCountText(variables.studiosGroundFloorWithGarden)}`,
                                  variables.studiosLowFloor > 0 && `kawalerki na niskich piętrach - ${getGeneralPropertyCountText(variables.studiosLowFloor)}`,
                                  variables.studiosHighFloor > 0 &&
                                      `kawalerki na wysokich piętrach - ${getGeneralPropertyCountText(variables.studiosHighFloor)}`,
                                  variables.studiosLastFloor > 0 &&
                                      `kawalerki na ostatnich piętrach - ${getGeneralPropertyCountText(variables.studiosLastFloor)}`,
                                  variables.studiosLastFloorWithTerrace > 0 &&
                                      `kawalerki na ostatnich piętrach z tarasem - ${getGeneralPropertyCountText(variables.studiosLastFloorWithTerrace)}`
                              ].filter(Boolean) as string[]
                          }
                      ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];
};
import {numberFormat, pluralize, priceFormat} from "@pg-mono/string-utils";

import {concatWithBrandName} from "../../../../app/utils/concat_with_brand_name";
import {getOfferToSaleSummarySectionTexts} from "./common_texts/get_offer_to_sale_summary_section_texts";
import {getReadyOffersSectionTexts} from "./common_texts/get_ready_offers_section_texts";
import {ILocationGenericSection, ILocationGenericSectionProps, ParagraphType} from "./constants";

export const studioOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        studioPriceMin: props.region.stats?.properties_studios_ranges_price_min || 0,
        studiosRangesPriceM2Avg: props.region.stats?.properties_studios_ranges_price_m2_avg || 0,
        studiosCountReady: props.region.stats?.properties_studios_count_ready || 0,
        studiosCountReadyInThreeMonths: props.region.stats?.properties_studios_count_ready_in_3m || 0,
        studiosCountReadyInSixMonths: props.region.stats?.properties_studios_count_ready_in_6m || 0,
        studiosCountReadyInOneYear: props.region.stats?.properties_studios_count_ready_in_12m || 0,
        studiosGroundFloor: props.region.stats?.properties_studios_ground_floor || 0,
        studiosGroundFloorWithGarden: props.region.stats?.properties_studios_ground_floor_with_garden || 0,
        studiosHighFloor: props.region.stats?.properties_studios_high_floor || 0,
        studiosLastFloor: props.region.stats?.properties_studios_last_floor || 0,
        studiosLastFloorWithTerrace: props.region.stats?.properties_studios_last_floor_with_terrace || 0,
        studiosLowFloor: props.region.stats?.properties_studios_low_floor || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        studiosOffersCountForSale: props.region.stats?.offers_studios_count_for_sale || 0,
        studiosPropertiesCountForSale: props.region.stats?.properties_studios_count_for_sale || 0
    };

    const getGeneralPropertyCountText = (propertiesCount: number) => {
        return `${propertiesCount} ${pluralize(["lokal", "lokale", "lokali"])(propertiesCount)}`;
    };

    if (!variables.studiosOffersCountForSale || !variables.studiosPropertiesCountForSale) {
        return [];
    }

    return [
        {
            headline: `Nowe kawalerki na sprzedaż ${variables.regionNameDeclensionWhere}`,
            paragraphs: [
                ...getOfferToSaleSummarySectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offerCountForSale: variables.studiosOffersCountForSale,
                    propertiesCountForSale: variables.studiosPropertiesCountForSale,
                    offerTypeName: "kawalerek"
                })
            ]
        },
        {
            headline: `Ile kosztują nowe kawalerki ${variables.regionNameDeclensionWhere}?`,
            paragraphs: [
                {
                    type: ParagraphType.TEXT,
                    text: [
                        variables.studioPriceMin > 0 &&
                            `Najtańsza kawalerka ${variables.regionNameDeclensionWhere} dostępna w sprzedaży kosztuje ${priceFormat(
                                variables.studioPriceMin
                            )}.`,
                        ` `,
                        variables.studiosRangesPriceM2Avg > 0 &&
                            `Aktualnie średnia cena za m2 kawalerki ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(
                                variables.studiosRangesPriceM2Avg
                            )}.`
                        // TODO: waiting to next stage
                        // ${getEstimatedPriceText(props.selectedRegionEstimatedPrices)}
                    ].filter(Boolean) as string[]
                },
                variables.promotionsCount > 0
                    ? {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie na rynku pierwotnym  ${variables.regionNameDeclensionWhere} ${pluralize([
                              `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                              `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                              `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
                          ])(variables.promotionsCount)} na kawalerki.`
                      }
                    : {
                          type: ParagraphType.TEXT,
                          text: `Aktualnie w inwestycjach ${variables.regionNameDeclensionWhere} nie obowiązują oferty specjalne na kawalerki.`
                      }
            ]
        },
        {
            headline: `${variables.regionName} - kawalerka gotowa do odbioru`,
            paragraphs: [
                ...getReadyOffersSectionTexts({
                    regionNameDeclensionWhere: variables.regionNameDeclensionWhere,
                    offersCountReady: variables.studiosCountReady,
                    offersCountReadyInThreeMonths: variables.studiosCountReadyInThreeMonths,
                    offersCountReadyInSixMonths: variables.studiosCountReadyInSixMonths,
                    offersCountReadyInOneYear: variables.studiosCountReadyInOneYear,
                    offerTypeNamePluralize: ["kawalerkę", "kawalerki", "kawalerek"],
                    offerTypeNameWithDeclensionOfReadyPluralize: ["kawalerka zostanie oddana", "kawalerki zostaną oddane", "kawalerek zostanie oddanych"],
                    offersLatestConstructionDate: variables.flatsLatestConstructionDate
                })
            ]
        },
        ...(variables.studiosGroundFloor > 0 ||
        variables.studiosGroundFloorWithGarden > 0 ||
        variables.studiosLowFloor > 0 ||
        variables.studiosHighFloor > 0 ||
        variables.studiosLastFloor > 0 ||
        variables.studiosLastFloorWithTerrace > 0
            ? ([
                  {
                      headline: `Oferty kawalerek dostępne na portalu`,
                      paragraphs: [
                          {
                              type: ParagraphType.TEXT,
                              text: `${concatWithBrandName("Na portalu")} dostępne są oferty kawalerek ${
                                  variables.regionNameDeclensionWhere
                              }, dostosowane do potrzeb odbiorców, w tym:`
                          },
                          {
                              type: ParagraphType.LIST,
                              text: [
                                  variables.studiosGroundFloor > 0 && `kawalerki na parterze - ${getGeneralPropertyCountText(variables.studiosGroundFloor)}`,
                                  variables.studiosGroundFloorWithGarden > 0 &&
                                      `kawalerki na parterze z ogródkiem - ${getGeneralPropertyCountText(variables.studiosGroundFloorWithGarden)}`,
                                  variables.studiosLowFloor > 0 && `kawalerki na niskich piętrach - ${getGeneralPropertyCountText(variables.studiosLowFloor)}`,
                                  variables.studiosHighFloor > 0 &&
                                      `kawalerki na wysokich piętrach - ${getGeneralPropertyCountText(variables.studiosHighFloor)}`,
                                  variables.studiosLastFloor > 0 &&
                                      `kawalerki na ostatnich piętrach - ${getGeneralPropertyCountText(variables.studiosLastFloor)}`,
                                  variables.studiosLastFloorWithTerrace > 0 &&
                                      `kawalerki na ostatnich piętrach z tarasem - ${getGeneralPropertyCountText(variables.studiosLastFloorWithTerrace)}`
                              ].filter(Boolean) as string[]
                          }
                      ]
                  }
              ] as ILocationGenericSection[])
            : [])
    ];
};
